import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { getHardcopyAccess, createHardcopyAccess, getStaff } from "../../lib/api";

export function formatAccessedAt(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    return d.toLocaleString("en-US", {
      month: "short", day: "numeric", year: "numeric",
      hour: "numeric", minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

export function HardCopyAccessTab({ studentId, studentName }) {
  const [logs, setLogs] = useState([]);
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [staffId, setStaffId] = useState("");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);

  const reload = async () => {
    try {
      const res = await getHardcopyAccess({ student_id: studentId });
      setLogs(res.data);
    } catch {
      toast.error("Failed to load hardcopy access logs");
    }
  };

  useEffect(() => {
    let alive = true;
    (async () => {
      await reload();
      try {
        const s = await getStaff();
        if (alive) setStaff([...(s.data || [])].sort((a, b) => (a.name || "").localeCompare(b.name || "")));
      } catch { /* non-fatal */ }
      if (alive) setLoading(false);
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studentId]);

  const openDialog = () => { setStaffId(""); setReason(""); setDialogOpen(true); };

  const confirm = async () => {
    if (!staffId) { toast.error("Select the staff member who accessed the record"); return; }
    setSaving(true);
    try {
      await createHardcopyAccess({ student_id: studentId, staff_id: staffId, reason });
      toast.success("Hardcopy access recorded");
      setDialogOpen(false);
      await reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to record access");
    }
    setSaving(false);
  };

  if (loading) return <div className="flex items-center justify-center h-32"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#00305E]" /></div>;

  return (
    <div className="space-y-4" data-testid="student-hardcopy-tab">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div>
              <CardTitle>Hard Copy Access</CardTitle>
              <CardDescription>{logs.length} recorded access{logs.length === 1 ? "" : "es"} of this student's physical record</CardDescription>
            </div>
            <Button onClick={openDialog} className="bg-[#00305E] hover:bg-[#00305E]/90" data-testid="record-hardcopy-access-btn">+ Record Hardcopy Access</Button>
          </div>
        </CardHeader>
        <CardContent>
          {logs.length === 0 ? (
            <p className="text-sm text-muted-foreground py-6 text-center">No hardcopy access has been recorded for this student yet.</p>
          ) : (
            <div className="space-y-3">
              {logs.map((l) => (
                <div key={l.id} className="border rounded-md p-3 space-y-1" data-testid={`hardcopy-access-${l.id}`}>
                  <div className="flex items-start justify-between gap-2 flex-wrap">
                    <div className="font-medium">{l.staff_name || "Unknown staff"}</div>
                    <div className="text-xs text-muted-foreground">{formatAccessedAt(l.accessed_at)}</div>
                  </div>
                  {l.reason && <div className="text-sm text-gray-700 whitespace-pre-wrap">Reason: {l.reason}</div>}
                  {l.recorded_by_name && <div className="text-xs text-muted-foreground">Recorded by {l.recorded_by_name}</div>}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md" data-testid="hardcopy-access-dialog">
          <DialogHeader>
            <DialogTitle>Record Hardcopy Access</DialogTitle>
            <DialogDescription>Log that a staff member accessed {studentName ? <strong>{studentName}</strong> : "this student"}'s physical record. The date and time are stamped automatically.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Staff member who accessed the record *</Label>
              <Select value={staffId} onValueChange={setStaffId}>
                <SelectTrigger data-testid="hardcopy-staff-select"><SelectValue placeholder="Select staff member" /></SelectTrigger>
                <SelectContent>
                  {staff.map((s) => (<SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Reason / purpose (optional)</Label>
              <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g., Reviewed for IEP meeting" data-testid="hardcopy-reason-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} disabled={saving}>Cancel</Button>
            <Button className="bg-[#00305E] hover:bg-[#00305E]/90" onClick={confirm} disabled={saving} data-testid="confirm-hardcopy-access-btn">{saving ? "Recording…" : "Confirm Access"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
